// デコのゆらぎ（しおれ⇔復活）。v0.6.0 の追加システム。
//
// 一部のデコ（光るキノコ等）は時間経過で「しおれる⇔復活する」サイクルを持つ。
// セルごとに seed + セル index から決まる周期・位相で、同じ state + 絶対時刻なら必ず
// 同じ結果（決定的）。だが周期と位相がセルごとに違うので、プレイヤーには予測しづらい。
//
// しおれ中は属性が減衰する（キノコの光・香りが 0 に）→ gardenAttributes に反映 →
// 来店する客が変わる。枯死はしない（撤去・再設置で位相が変わる＝テコ入れ手段）。
//
// 設計（決定性の担保）: ゆらぎは「絶対時刻 ms の周期関数」として表す。乱数も累積状態も
// 使わず、ms を入れれば 0..1 の活力（vitality）が一意に返る。simulate は来店刻みの atMs
// から活力を評価するので、刻みの分割に依存しない（合成可能）。
//
// この層は描画・DOM・乱数（Math.random）に触れない純データ＋純関数。

import { hashSeed } from './rng';

/** ゆらぎを持つデコ ID（ここに無いデコは常に元気＝活力 1.0）。 */
export const WILTING_DECO_IDS: ReadonlySet<string> = new Set(['glow_mushroom']);

/** デコがゆらぐ（しおれ⇔復活する）か。 */
export function isWiltingDeco(decoId: string): boolean {
  return WILTING_DECO_IDS.has(decoId);
}

/** 周期の下限・上限（ミリ秒）。3〜8 時間でセルごとに散らす。 */
const PERIOD_MIN_MS = 3 * 3600 * 1000;
const PERIOD_MAX_MS = 8 * 3600 * 1000;

/**
 * セルの (seed, index) から、周期(ms)・位相(0..1) を決定的に導く。
 * hashSeed でセル固有の 32bit を作り、その上位/下位ビットから周期と位相に割り当てる。
 */
function cellCycle(seed: number, cellIndex: number): { periodMs: number; phase: number } {
  const h = (hashSeed(`wilt:${cellIndex}`) ^ (seed >>> 0)) >>> 0;
  // 周期: 上位ビットを [0,1) に正規化して [MIN, MAX] に写す。
  const u = (h >>> 8) / 0xffffff; // 24bit → [0,1)
  const periodMs = PERIOD_MIN_MS + u * (PERIOD_MAX_MS - PERIOD_MIN_MS);
  // 位相: 下位 8bit を [0,1) に。
  const phase = (h & 0xff) / 256;
  return { periodMs, phase };
}

/**
 * 「元気でいる時間の割合」(duty cycle)。1 周期のうち 60〜80% は元気にする
 * （要望: 60〜80% は元気）。セルごとに少し散らす（seed 由来）。
 */
function dutyFor(seed: number, cellIndex: number): number {
  const h = (hashSeed(`duty:${cellIndex}`) ^ (seed >>> 0)) >>> 0;
  return 0.6 + ((h >>> 16) & 0xff) / 255 * 0.2; // 0.6..0.8
}

/**
 * セルの活力（vitality, 0..1）を絶対時刻から算出する純関数。
 * 1.0 = 完全に元気、0.0 = 完全にしおれ。間は遷移（ぽんっと戻る/しぼむ演出に使える）。
 *
 * 1 周期のうち duty ぶんは満活力(1.0)、残りがしおれ区間。しおれ区間の中央で最も
 * しおれ(0.0)、その前後はなだらかに遷移する（cos でやわらかく）。
 *
 * @param decoId    デコ ID（ゆらがないデコは常に 1.0）。
 * @param cellIndex 庭セル index（位相・周期の素）。
 * @param seed      ゲーム seed（位相・周期の素）。
 * @param ms        絶対時刻（エポックミリ秒）。
 */
export function vitalityAt(decoId: string, cellIndex: number, seed: number, ms: number): number {
  if (!isWiltingDeco(decoId)) return 1.0;
  const { periodMs, phase } = cellCycle(seed, cellIndex);
  const duty = dutyFor(seed, cellIndex);
  // 周期内の位置 0..1。
  const t = (((ms / periodMs + phase) % 1) + 1) % 1;
  if (t < duty) return 1.0; // 元気区間
  // しおれ区間 [duty, 1) を 0..1 に正規化。区間の中央(0.5)で最もしおれる。
  const w = (t - duty) / (1 - duty); // 0..1
  // cos の山: w=0/1 で 1.0（元気の境）、w=0.5 で 0.0（最しおれ）。
  const vit = (1 - Math.cos(w * Math.PI * 2)) / 2; // 0→1→0 ではなく 1→0→1 にしたい
  // (1-cos(2πw))/2 は w=0→0, w=0.5→1, w=1→0。これを反転して「境で元気・中央でしおれ」に。
  return 1 - vit;
}

/** しおれ判定のしきい値（活力がこれ未満なら「しおれている」とみなす）。 */
export const WILT_THRESHOLD = 0.5;

/** セルがしおれているか（見た目・サマリ文言の判定に使う）。 */
export function isWilted(decoId: string, cellIndex: number, seed: number, ms: number): boolean {
  return isWiltingDeco(decoId) && vitalityAt(decoId, cellIndex, seed, ms) < WILT_THRESHOLD;
}
